import { slugifyCompanyName } from '../utils/convert';

// .env
export const NEXT_PUBLIC_IMAGE_URL = process.env.NEXT_PUBLIC_IMAGE_URL;
export const NEXT_PUBLIC_IMAGE_CDN_URL = process.env.NEXT_PUBLIC_IMAGE_CDN_URL;
export const NEXT_PUBLIC_IMAGE_UPZI_URL =
  process.env.NEXT_PUBLIC_IMAGE_UPZI_URL;

function normalizePathPrefix(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;

  const trimmed = value.trim().replace(/^\/+|\/+$/g, '');

  return trimmed ? `/${trimmed}` : '';
}

/** Lấy path prefix từ NEXT_PUBLIC_BASE_URL (vd: https://www.dev.upzi.vn/thao-luan → /thao-luan). */
function getAssetPrefixFromBaseUrl(): string | undefined {
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL?.trim();

  if (!baseUrl) return undefined;

  try {
    return normalizePathPrefix(new URL(baseUrl).pathname);
  } catch {
    return undefined;
  }
}

/**
 * Prefix public cho route và static assets (vd: /thao-luan, /community).
 * Ưu tiên NEXT_PUBLIC_ASSET_PREFIX, NEXT_PUBLIC_BASE_PATH, pathname của NEXT_PUBLIC_BASE_URL.
 */
export const BASE_ASSETS_PATH =
  normalizePathPrefix(process.env.NEXT_PUBLIC_ASSET_PREFIX) ??
  normalizePathPrefix(process.env.NEXT_PUBLIC_BASE_PATH) ??
  getAssetPrefixFromBaseUrl() ??
  '/community';

/** Next.js basePath — gateway strip prefix trước khi tới app. */
export const BASE_PATH = '';
/** PostHog ingest host cho client SDK và direct capture. */
export const POSTHOG_API_HOST = (
  process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
).replace(/\/$/, '');

/** Internal app route (no BASE_PATH). */
export const TAI_UNG_DUNG_PATH = '/tai-ung-dung';
export const DANH_SACH_CHIEN_DICH_PATH = '/danh-sach-chien-dich';
export const CHI_TIET_CHIEN_DICH_PATH = '/chi-tiet-chien-dich';
export const CONG_TY_PATH = '/cong-ty';

export function getCampaignListPath(): string {
  return getAppRoute(DANH_SACH_CHIEN_DICH_PATH);
}

export function getCampaignPreviewPath(campaignId: string): string {
  return getAppRoute(
    `${DANH_SACH_CHIEN_DICH_PATH}?preview=${encodeURIComponent(campaignId)}`
  );
}

export function getCampaignDetailPagePath(campaignId: string): string {
  return getAppRoute(
    `${CHI_TIET_CHIEN_DICH_PATH}/${encodeURIComponent(campaignId)}`
  );
}

export function getCompanyPagePath(
  companyName: string,
  companyId: string
): string {
  const slug = slugifyCompanyName(companyName);
  const id = companyId.trim();

  if (!slug || !id) {
    return CONG_TY_PATH;
  }

  return `${CONG_TY_PATH}/${slug}-${id}`;
}

export function getCampaignAppliedPath(): string {
  return getAppRoute(`${DANH_SACH_CHIEN_DICH_PATH}?applied=1`);
}

/** Full path for <a href> / window.location (includes BASE_PATH). */
export function getAppRoute(path: string = TAI_UNG_DUNG_PATH): string {
  return withBasePath(path);
}

/** Prefix app routes and public assets with BASE_PATH (e.g. /community/tai-ung-dung). */
export function withBasePath(path: string): string {
  const normalized = `/${path.replace(/^\/+/, '')}`;

  if (
    normalized === BASE_ASSETS_PATH ||
    normalized.startsWith(`${BASE_ASSETS_PATH}/`)
  ) {
    return normalized;
  }

  return `${BASE_ASSETS_PATH}${normalized}`;
}

/** Build absolute public URL for a route, respecting NEXT_PUBLIC_BASE_URL. */
export function getPublicUrl(path: string): string {
  const publicPath = withBasePath(path);
  const base = process.env.NEXT_PUBLIC_BASE_URL?.trim();

  if (!base) return publicPath;

  try {
    return `${new URL(base).origin}${publicPath}`;
  } catch {
    return publicPath;
  }
}

/** Absolute URL for a file in /public, including the asset prefix crawlers need. */
function getPublicAssetUrl(path: string): string {
  const assetPath = withBasePath(path);
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL?.trim();

  if (!baseUrl) return assetPath;

  try {
    return `${new URL(baseUrl).origin}${assetPath}`;
  } catch {
    return assetPath;
  }
}

/** Absolute URL for Open Graph / Twitter share previews. */
export const OG_IMAGE_URL = getPublicAssetUrl('/images/upzi_og_image.png');
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

// image
export const WARN_ICON = 'image/vnws/web/upload/warning.png';
export const UPZI_MASCOT_BLANK = 'image/upzi/mascot/mascot_blank.png';
export const UPZI_MASCOT_FLIP_TABLE = 'image/upzi/mascot/mascot_flip_table.png';

// cookie
export const UPZI_ACCESS_TOKEN = 'UPZI_ACCESS_TOKEN';
export const UPZI_REFRESH_TOKEN = 'UPZI_REFRESH_TOKEN';
export const UPZI_REFRESH_BACKOFF = 'UPZI_REFRESH_BACKOFF';
/** @deprecated Legacy cookie name — still read as fallback during migration */
export const VNWS_ACCESS_TOKEN = 'VNWS_ACCESS_TOKEN';
/** @deprecated Legacy cookie name — still read as fallback during migration */
export const VNWS_REFRESH_TOKEN = 'VNWS_REFRESH_TOKEN';
export const VNWS_USER_ID = 'VNWS_USER_ID';

// link download app
export const URL_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.vietnamworks.vietnamworks&referrer=utm_source%3Demail%26utm_campaign%3Dnestle_application_1';
export const URL_APP_STORE =
  'https://apps.apple.com/app/apple-store/id1180866051?pt=1608090&ct=email_nestle_application_1&mt=8';

export const UPZI_LOGIN_SUCCESS_EVENT_NAME = 'upzi:login-success';
export const UPZI_OPEN_LOGIN_POPUP_EVENT_NAME = 'upzi:open-login-popup';
export const JS_VIEW_DOWNLOAD_APP_POPUP_EVENT_NAME =
  'js_view_download_app_popup';

export const JS_VIEW_OPPORTUNITY_DETAIL_EVENT_NAME =
  'js_view_opportunity_detail';

export const JS_VIEW_POST_DETAIL_EVENT_NAME = 'js_view_post_detail';
export const JS_POST_DETAIL_SOURCE_SCREEN = 'js_post_detail';
export const JS_REACTION_POST_COMMENT_EVENT_NAME = 'js_reaction_post_comment';
export const JS_COMMENT_START_EVENT_NAME = 'js_comment_start';
export const JS_COMMENT_COMPLETE_EVENT_NAME = 'js_comment_complete';
export const JS_COMMENT_CANCEL_EVENT_NAME = 'js_comment_cancel';
export const JS_SHARE_POST_EVENT_NAME = 'js_share_post';
export const JS_CAMPAIGN_SHARE_EVENT_NAME = 'js_campaign_share';
export const JS_VIEW_CAMPAIGN_DETAIL_EVENT_NAME = 'js_view_campaign_detail';
export const JS_CAMPAIGN_CLICK_TO_APPLY_EVENT_NAME =
  'js_campaign_click_to_apply';
export const JS_CAMPAIGN_APPLY_START_EVENT_NAME = 'js_campaign_apply_start';
export const JS_CAMPAIGN_APPLY_SUBMIT_EVENT_NAME = 'js_campaign_apply_submit';
export const JS_CAMPAIGN_APPLY_SUCCESS_EVENT_NAME = 'js_campaign_apply_success';
export const JS_CAMPAIGN_APPLY_FAIL_EVENT_NAME = 'js_campaign_apply_fail';
export const JS_VIEW_CAMPAIGN_LIST_EVENT_NAME = 'js_view_campaign_list';
export const JS_CAMPAIGN_IMPRESSION_EVENT_NAME = 'js_campaign_impression';
export const JS_CAMPAIGN_LIST_SOURCE_SCREEN = 'js_campaign_list';
export const JS_CAMPAIGN_DETAIL_SOURCE_SCREEN = 'js_campaign_detail';
export const JS_COMMUNITY_FEED_SOURCE_SCREEN = 'js_community_feed';
export const JS_CAMPAIGN_FEED_SOURCE_SECTION = 'js_campaign_feed';
export const JS_DISMISS_CAMPAIGN_EVENT_NAME = 'js_dismiss_campaign';
export const JS_VIEW_COMPANY_PROFILE_EVENT_NAME = 'js_view_company_profile';
export const JS_VIEW_MORE_COMMENT_EVENT_NAME = 'js_view_more_comment';
export const JS_VOTE_ON_POST_EVENT_NAME = 'js_vote_on_post';
export const JS_INIT_NEW_POST_EVENT_NAME = 'js_init_new_post';
export const JS_CLOSE_NEW_POST_EVENT_NAME = 'js_close_new_post';
export const JS_FINISH_NEW_POST_EVENT_NAME = 'js_finish_new_post';
export const JS_VISIT_FEED_EVENT_NAME = 'js_visit_feed';
export const JS_REWRITE_PROFILE_START_EVENT_NAME = 'js_rewrite_profile_start';
export const JS_REWRITE_PROFILE_SUCCESS_EVENT_NAME =
  'js_rewrite_profile_success';
export const JS_EDIT_PROFILE_EVENT_NAME = 'js_edit_profile';
export const CV_BUILDER_SOURCE_SCREENING = 'cv_builder';
export const REWRITTEN_PROFILE_FLOW = 'rewritten';
export const HIDE_SALARY = 'Lương không công khai';

export const OAUTH_OBJ = {
  ACCESS_TOKEN: 'oauth_access_token',
  EMAIL: 'email',
  GIVEN_NAME: 'given_name',
  FAMILY_NAME: 'family_name',
  REDIRECT_TO: 'redirect_to',
  VERIFIED_EMAIL_USER: 'verified_email_user',
};

export const USER_AGENT_OPPORTUNITY_PAGE = 'vnws-opportunity-page/1.0';
